import { ExTooltip, TooltipAlignment } from '@boomi/exosphere';
import { useEffect, useMemo, useState } from 'react';

const SECOND_IN_MS = 1000;
const MINUTE_IN_MS = SECOND_IN_MS * 60;

const formatRelativeTime = (date: Date): string => {
    const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
    const diffInMinutes = Math.floor((Date.now() - date.getTime()) / MINUTE_IN_MS);
    const diffInHours = Math.floor(diffInMinutes / 60);

    if (diffInMinutes <= 5) {
        return 'moments ago';
    } else if (diffInMinutes < 60) {
        return rtf.format(-diffInMinutes, 'minute');
    } else {
        return rtf.format(-diffInHours, 'hour');
    }
};

const getRefreshInterval = (date: Date): number => {
    const diffInMinutes = Math.floor((Date.now() - date.getTime()) / MINUTE_IN_MS);

    if (diffInMinutes <= 5) {
        return SECOND_IN_MS * 30;
    } else if (diffInMinutes < 60) {
        return MINUTE_IN_MS;
    } else {
        return MINUTE_IN_MS * 30;
    }
};

const DateWithTooltip = ({
    dateString,
    alignment = TooltipAlignment.MIDDLE,
}: {
    dateString: string;
    alignment?: TooltipAlignment;
}) => {
    const date = useMemo(() => new Date(dateString), [dateString]);
    const now = new Date();
    const isToday =
        now.toLocaleString(undefined, { dateStyle: 'short' }) ===
        date.toLocaleString(undefined, { dateStyle: 'short' });
    const isThisYear = now.getFullYear() === date.getFullYear();

    const [, setRefreshTick] = useState(0);

    useEffect(() => {
        if (!isToday) {
            return;
        }

        let currentTimer: number | undefined;

        const refreshDate = () => {
            const interval = getRefreshInterval(date);
            currentTimer = window.setTimeout(() => {
                setRefreshTick((prev) => prev + 1);
                refreshDate();
            }, interval);
        };

        refreshDate();

        return () => clearTimeout(currentTimer);
    }, [date, isToday]);

    return (
        <ExTooltip alignment={alignment} className="date-with-tooltip">
            {date.toLocaleString(undefined, {
                dateStyle: 'long',
                timeStyle: 'medium',
            })}
            <span slot="anchor">
                {isToday
                    ? formatRelativeTime(date)
                    : date.toLocaleString(undefined, {
                          day: 'numeric',
                          month: 'short',
                          year: isThisYear ? undefined : 'numeric',
                      })}
            </span>
        </ExTooltip>
    );
};

export default DateWithTooltip;
